import { lazy } from "react"
import { createBrowserRouter, Navigate, useLocation } from "react-router"
import App from "./App.tsx"
import { DashboardLayout, LazyRoute } from "./layouts"
import { nucleusWorkflowsEnabled } from "./configs/nucleusWorkflows"
// Eager on purpose: the error view must not depend on a dynamic import that
// could itself fail (e.g. stale chunk after a deploy).
import { RouteErrorView } from "./components/RouteErrorView"
// Tiny constants (no component code) driving the pinned Demand routes below.
import {
  DEMAND_BASE_PATH,
  DEMAND_REPORT_SLUG,
} from "./containers/ReportingView/constants"
// The gold `class` values the two class pages scope to — kept beside the
// cubes' own split column so the router cannot drift from the data.
import {
  DRINK_MIX_CLASS,
  SPARKLING_CLASS,
} from "./containers/ReplenishmentView/constants"

// PERF: non-default routes stay lazy — smaller initial bundle.
const MappingDetailView = lazy(() =>
  import("./containers/MappingDetailView").then((m) => ({ default: m.MappingDetailView })),
)
const DataSourceDetailView = lazy(() =>
  import("./containers/DataSourceDetailView").then((m) => ({ default: m.DataSourceDetailView })),
)
const RunHistoryView = lazy(() =>
  import("./containers/RunHistoryView").then((m) => ({ default: m.RunHistoryView })),
)
const ReportingView = lazy(() =>
  import("./containers/ReportingView").then((m) => ({ default: m.ReportingView })),
)
const BalanceWalkView = lazy(() =>
  import("./containers/BalanceWalkView").then((m) => ({ default: m.BalanceWalkView })),
)
const TrackerView = lazy(() =>
  import("./containers/TrackerView").then((m) => ({ default: m.TrackerView })),
)
const AllocationEngineView = lazy(() =>
  import("./containers/AllocationEngineView").then((m) => ({
    default: m.AllocationEngineView,
  })),
)
const ReplenishmentView = lazy(() =>
  import("./containers/ReplenishmentView").then((m) => ({
    default: m.ReplenishmentView,
  })),
)
const DemandReportingView = lazy(() =>
  import("./containers/DemandReportingView").then((m) => ({
    default: m.DemandReportingView,
  })),
)
const MasterDataView = lazy(() =>
  import("./containers/MasterDataView").then((m) => ({ default: m.MasterDataView })),
)
const TicketsView = lazy(() =>
  import("./containers/TicketsView/TicketsView").then((m) => ({
    default: m.TicketsView,
  })),
)
const DigitalTwinView = lazy(() =>
  import("./containers/DigitalTwinView").then((m) => ({ default: m.DigitalTwinView })),
)
const LinearView = lazy(() =>
  import("./containers/LinearView").then((m) => ({ default: m.LinearView })),
)
const NucleusWorkflowsView = lazy(() =>
  import("./containers/NucleusWorkflowsView").then((m) => ({ default: m.NucleusWorkflowsView })),
)
const DocsView = lazy(() =>
  import("./containers/DocsView").then((m) => ({ default: m.DocsView })),
)
// Admin (user management) — atomic-users only; the view itself redirects others.
const UserAdminView = lazy(() =>
  import("./containers/UserAdminView/UserAdminView").then((m) => ({ default: m.UserAdminView })),
)

/** The app's landing redirect. The query string rides along so a `?prompt=`
 *  Nucleus deep link still works: DashboardLayout opens the dock on any page
 *  that carries it and AssistantChatView consumes the param. (It used to
 *  land on /allocation-engine from the days the chat lived inside the core
 *  embed there — the dock is native now, so the landing page is the same
 *  with or without a prompt.) */
const IndexRedirect = () => {
  const { search } = useLocation()
  return <Navigate to={{ pathname: "/data", search }} replace />
}

/** The old full-page Nucleus workspace (`/nucleus`, the AppSync event-stream
 *  chat) is gone; the core-served assistant lives in the dock on every page.
 *  Old links land on the landing page with the dock open. */
const NucleusRedirect = () => {
  const { search } = useLocation()
  return <Navigate to={{ pathname: "/data", search }} state={{ openNucleus: true }} replace />
}

/** `/tracker` → `/trackers` with the query string intact. */
const TrackerRedirect = () => {
  const { search } = useLocation()
  return <Navigate to={{ pathname: "/trackers", search }} replace />
}

const router = createBrowserRouter([
  {
    path: "/",
    element: <App />,
    errorElement: <RouteErrorView />,
    children: [
      // Cognito OAuth redirect routes — Amplify processes auth tokens on these pages
      { path: "callback", element: <Navigate to="/" replace /> },
      { path: "logout", element: <Navigate to="/" replace /> },
      {
        element: <DashboardLayout />,
        children: [
          { index: true, element: <IndexRedirect /> },
          // The Data Hub landing page is retired — /data (the master-data
          // embed) covers it, with source connections on /data#connections.
          // Old links and bookmarks land there. The mapping-folder and
          // source drill-downs below stay routable until their editors have
          // a home inside the embed.
          { path: "data-hub", element: <Navigate to="/data#connections" replace /> },
          {
            path: "data-hub/source/:name",
            element: (
              <LazyRoute>
                <DataSourceDetailView />
              </LazyRoute>
            ),
          },
          {
            path: "data-hub/:folder",
            element: (
              <LazyRoute>
                <MappingDetailView />
              </LazyRoute>
            ),
          },
          { path: "master-data", element: <Navigate to="/data" replace /> },
          {
            path: "data",
            element: (
              <LazyRoute>
                <MasterDataView />
              </LazyRoute>
            ),
          },
          {
            path: "digital-twin",
            element: (
              <LazyRoute>
                <DigitalTwinView />
              </LazyRoute>
            ),
          },
          {
            path: "linear",
            element: (
              <LazyRoute>
                <LinearView />
              </LazyRoute>
            ),
          },
          // Demand primary tab → the native pivot, reading core's demand
          // slice (static route wins over demand/:reportSlug below by route
          // ranking). The slugged reports stay on the reporting embed.
          {
            path: "demand",
            element: (
              <LazyRoute>
                <DemandReportingView />
              </LazyRoute>
            ),
          },
          {
            // Slugged demand reports keep serving the reporting embed pinned
            // to Sales Forecast so in-frame report nav stays under /demand,
            // mirroring reporting/:reportSlug?.
            path: "demand/:reportSlug",
            element: (
              <LazyRoute>
                <ReportingView
                  reportSlug={DEMAND_REPORT_SLUG}
                  basePath={DEMAND_BASE_PATH}
                />
              </LazyRoute>
            ),
          },
          {
            // Direct-nav only (no sidebar entry): every area's Linear tickets
            // (not just Master Data) — the masterdata embed opened on its
            // standalone tickets page, with a Category column + filter; company
            // bound by the embed token.
            path: "tickets",
            element: (
              <LazyRoute>
                <TicketsView />
              </LazyRoute>
            ),
          },
          {
            // Plan Tracker — the shared route every client app exposes at /trackers
            // (core deep-links it: ?type=&scenario=&upload_group=&chart=<json>).
            path: "trackers",
            element: (
              <LazyRoute>
                <TrackerView />
              </LazyRoute>
            ),
          },
          // Old spelling: keep every bookmark and link alive, query intact.
          { path: "tracker", element: <TrackerRedirect /> },
          {
            // Direct-nav only (no sidebar entry): the Allocation Engine, served
            // whole by atomic-core as an iframe embed. The page — module
            // selector, precompiled inputs, the run, and its balance walk /
            // allocations / validations — lives there; we supply the route and
            // the token mint. Running the engine is gated to ROOT/ADMIN at that
            // mint, so the route needs no guard of its own.
            path: "allocation-engine",
            element: (
              <LazyRoute>
                <AllocationEngineView />
              </LazyRoute>
            ),
          },
          {
            // Direct-nav only (no sidebar entry): the master-data dependency
            // tree, rendered by @atomic/balance-walk-engine.
            path: "balance-walk",
            element: (
              <LazyRoute>
                <BalanceWalkView />
              </LazyRoute>
            ),
          },
          {
            // LMNT's independent replenishment results surface. Keep Core's
            // work-in-progress allocation workbench isolated on /allocation-engine.
            // One route object for Balance Walk and the two report tabs (Build
            // Plan, Transfer Plan), so switching tabs keeps the header, the run
            // selector, a run in progress AND the views themselves mounted. A
            // splat, not `:tab?`: the page renders each view under descendant
            // <Routes> (a hidden view reads its own frozen location), which the
            // router only expects below a route that ends in `*`.
            path: "replenishment/*",
            element: (
              <LazyRoute>
                <ReplenishmentView />
              </LazyRoute>
            ),
          },
          {
            // Supply — the Replenishment Balance Walk on its own page. The
            // full page stays routed at /replenishment (no tile); this is the
            // rail's cut of it.
            path: "supply/*",
            element: (
              <LazyRoute>
                <ReplenishmentView basePath="/supply" mode="walk" title="Supply" />
              </LazyRoute>
            ),
          },
          {
            // The per-class planning pages: Production + Transfers, scoped to
            // one product class each, with independently locked plans (one
            // class's committed horizon can run weeks ahead of the other's).
            path: "sparkling/*",
            element: (
              <LazyRoute>
                <ReplenishmentView basePath="/sparkling" mode="plan" lockedClass={SPARKLING_CLASS} />
              </LazyRoute>
            ),
          },
          {
            path: "drink-mix/*",
            element: (
              <LazyRoute>
                <ReplenishmentView basePath="/drink-mix" mode="plan" lockedClass={DRINK_MIX_CLASS} />
              </LazyRoute>
            ),
          },
          {
            // Optional :reportSlug — bare tab and deep links share one route
            // object so in-frame report navigation never remounts the iframe.
            // Optional :qfSlug carries a quick filter as a path segment
            // (/reporting/TimeSeries/SalesForecast), mirroring queryviz's own
            // canonical report paths.
            path: "reporting/:reportSlug?/:qfSlug?",
            element: (
              <LazyRoute>
                <ReportingView />
              </LazyRoute>
            ),
          },
          {
            path: "runs/history",
            element: (
              <LazyRoute>
                <RunHistoryView />
              </LazyRoute>
            ),
          },
          {
            path: "docs",
            element: (
              <LazyRoute>
                <DocsView />
              </LazyRoute>
            ),
          },
          ...(nucleusWorkflowsEnabled
            ? [
                {
                  path: "workflows",
                  element: (
                    <LazyRoute>
                      <NucleusWorkflowsView />
                    </LazyRoute>
                  ),
                },
              ]
            : []),
          { path: "nucleus", element: <NucleusRedirect /> },
          {
            path: "user-admin",
            element: (
              <LazyRoute>
                <UserAdminView />
              </LazyRoute>
            ),
          },
          // Catch-all: unknown URLs render a branded 404 inside the normal
          // app chrome instead of bubbling to the bare root errorElement.
          { path: "*", element: <RouteErrorView /> },
        ],
      },
    ],
  },
])

export default router
